import React from 'react';
import { CreditCard, Calendar, ExternalLink, CheckCircle, Euro, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const PLANITY_URL = 'https://www.planity.com/hilanta-13004-marseille';

// Plans d'abonnement (information only)
const PLANS = [
  { id: 'A', massages: 3, duration: 60, price: 200, savings: 25 },
  { id: 'B', massages: 5, duration: 60, price: 330, savings: 45, popular: true },
  { id: 'C', massages: 10, duration: 60, price: 660, savings: 90 }
];

const SubscriptionsSimple = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-sand-light" data-testid="subscriptions-page">
      <title>Abonnements Massage | Hilanta - Forfaits Bien-être Marseille</title>
      <meta name="description" content="Économisez avec nos abonnements massage chez Hilanta à Marseille. Forfaits 3, 5 ou 10 massages." />
      
      {/* Header */}
      <section className="py-8 sm:py-12 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CreditCard className="mx-auto text-terracotta mb-4" size={40} />
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-warm-brown mb-4">
            Nos Abonnements
          </h1>
          <p className="font-body text-sm sm:text-base text-warm-brown-light max-w-2xl mx-auto">
            Économisez avec nos forfaits massages - Souscription sur place ou via Planity
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl p-6 border-2 ${
                  plan.popular
                    ? 'border-orange-400 shadow-xl'
                    : 'border-orange-100 shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-xs font-body px-4 py-1 rounded-full flex items-center gap-1">
                      <Star size={12} /> Populaire
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="font-heading text-2xl text-warm-brown mb-2">
                    Abonnement {plan.id}
                  </h3>
                  <div className="flex items-center justify-center gap-2 text-orange-500">
                    <span className="font-heading text-4xl font-bold">
                      {plan.price}
                    </span>
                    <Euro size={24} />
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-warm-brown">
                    <CheckCircle size={20} className="text-orange-500 flex-shrink-0" />
                    <span className="font-body">
                      <strong>{plan.massages}</strong> massages de {plan.duration} min
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-warm-brown">
                    <CheckCircle size={20} className="text-orange-500 flex-shrink-0" />
                    <span className="font-body">
                      Économie de <strong>{plan.savings} €</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-warm-brown">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                    <span className="font-body">
                      Valable <strong>1 an</strong>
                    </span>
                  </div>
                </div>
                
                <div className="text-center pt-4 border-t border-sand">
                  <p className="font-body text-sm text-warm-brown-light">
                    soit <strong>{Math.round(plan.price / plan.massages)} €</strong> / massage
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Planity */}
      <section className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-terracotta to-terracotta-dark text-white p-6 text-center">
              <Calendar className="mx-auto mb-3" size={36} />
              <h2 className="font-heading text-2xl mb-2">Comment souscrire ?</h2>
              <p className="font-body text-white/90">
                Souscrivez à votre abonnement sur Planity ou directement sur place
              </p>
            </div>
            
            {/* Content */}
            <div className="p-6 sm:p-8">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={22} />
                  <span className="font-body text-warm-brown">
                    <strong>Paiement sur place</strong> lors de votre visite
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={22} />
                  <span className="font-body text-warm-brown">
                    <strong>Conseils personnalisés</strong> pour choisir votre forfait
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={22} />
                  <span className="font-body text-warm-brown">
                    <strong>Carte d'abonnement</strong> nominative
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="text-amber-500 flex-shrink-0 mt-0.5" size={22} />
                  <span className="font-body text-warm-brown">
                    <strong>Valable 1 an</strong> à partir de la souscription
                  </span>
                </li>
              </ul>
              
              <a 
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button 
                  className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-full py-6 text-lg font-body"
                  data-testid="planity-subscription-btn"
                >
                  <Calendar className="mr-2" size={22} />
                  Souscrire sur Planity
                  <ExternalLink className="ml-2" size={18} />
                </Button>
              </a>
              
              <div className="mt-4 flex items-center justify-center gap-2 text-warm-brown-light">
                <Euro size={16} />
                <span className="font-body text-sm">Paiement sur place uniquement</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-8 bg-sand/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-warm-brown-light text-sm mb-2">
            Besoin d'aide pour choisir ? Appelez-nous
          </p>
          <a href="tel:+33960421822" className="text-terracotta font-heading text-2xl hover:underline">
            09 60 42 18 22
          </a>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionsSimple;
